import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getEncuestas,
  getEncuesta,
  crearEncuesta,
  actualizarEncuesta,
  eliminarEncuesta,
  toggleEncuesta,
  exportarExcel,
  descargarQR,
} from '../api/encuestas.api'
import type { EncuestaCreatePayload, EncuestaUpdatePayload } from '../types'

export const encuestasKeys = {
  all: ['encuestas'] as const,
  list: (params?: Record<string, unknown>) => [...encuestasKeys.all, 'list', params] as const,
  detail: (id: number) => [...encuestasKeys.all, 'detail', id] as const,
}

export function useEncuestas(params?: { page?: number; page_size?: number; search?: string; grupo_laboratorio_id?: number }) {
  return useQuery({
    queryKey: encuestasKeys.list(params),
    queryFn: () => getEncuestas(params),
  })
}

export function useEncuestaDetail(id: number) {
  return useQuery({
    queryKey: encuestasKeys.detail(id),
    queryFn: () => getEncuesta(id),
    enabled: id > 0,
  })
}

export function useCrearEncuesta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: EncuestaCreatePayload) => crearEncuesta(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: encuestasKeys.all })
      toast.success('Encuesta creada correctamente')
    },
    onError: () => toast.error('Error al crear encuesta'),
  })
}

export function useActualizarEncuesta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: EncuestaUpdatePayload }) =>
      actualizarEncuesta(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: encuestasKeys.all })
      queryClient.invalidateQueries({ queryKey: encuestasKeys.detail(id) })
      toast.success('Encuesta actualizada')
    },
    onError: () => toast.error('Error al actualizar encuesta'),
  })
}

export function useEliminarEncuesta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => eliminarEncuesta(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: encuestasKeys.all })
      toast.success('Encuesta eliminada')
    },
    onError: () => toast.error('Error al eliminar encuesta'),
  })
}

export function useToggleEncuesta() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => toggleEncuesta(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: encuestasKeys.all })
    },
  })
}

export function useExportarExcel() {
  return useMutation({
    mutationFn: (id: number) => exportarExcel(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `encuesta_export_${new Date().toISOString().slice(0, 10)}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Excel descargado')
    },
    onError: () => toast.error('Error al exportar Excel'),
  })
}

export function useDescargarQR() {
  return useMutation({
    mutationFn: (id: number) => descargarQR(id),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `qr_encuesta_${new Date().toISOString().slice(0, 10)}.png`
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al descargar QR'),
  })
}
